import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getEffectiveDatesOptions } from 'global/services-helpers/domainService';
import { ddnjSICWarningMessage } from 'global/utils/sponsor-validations/ddnj/sicValidation';
import { makeAutoObservable, reaction } from 'mobx';
import { ReactNode } from 'react';
import { DomainProductLinesDelegate } from 'store/modules/initiate-quote/domainProductLinesDelegate';
import { getRootStoreInstance, RootStore } from 'store/rootStore';

type DDNJQuoteProposalInfoFields = {
  proposalName: FormControl<string>;
  effectiveDate: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
  sic4: FormControl<string>;
};

export class DDNJQuoteProposalInfoFormDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  public readonly domainProductLinesDelegate: DomainProductLinesDelegate;
  public proposalForm: FormController<DDNJQuoteProposalInfoFields>;

  public get effectiveDatesOptions(): OptionType[] {
    return getEffectiveDatesOptions(this._rootStore.domainStoreV2.effectiveDates);
  }

  public get isValid(): boolean {
    return (
      this.proposalForm.basicFieldsValidationsPassed &&
      !this.proposalForm.isLoading &&
      !this.domainProductLinesDelegate.isLoading &&
      // Covered lines are not locked on this page, so this is domain-available GROUP lines only.
      this.domainProductLinesDelegate.hasQuotableProductLine
    );
  }

  /** Shown as a warning `Alert` when the current SIC matches a non-blocking rule. */
  public get sicWarningMessage(): ReactNode | null {
    const sicValue = this.proposalForm.fields.sic4.value;
    return sicValue && sicValue.length === 4 ? ddnjSICWarningMessage(sicValue) : null;
  }

  private get _canFetchProductLines(): boolean {
    const { effectiveDate, zip, state, county, sic4 } = this.proposalForm.fields;

    return !!(
      !effectiveDate.unpassedValidationMsg &&
      !zip.unpassedValidationMsg &&
      !state.unpassedValidationMsg &&
      !county.unpassedValidationMsg &&
      !sic4.unpassedValidationMsg
    );
  }

  constructor(domainProductLinesDelegate: DomainProductLinesDelegate) {
    this.domainProductLinesDelegate = domainProductLinesDelegate;
    this.proposalForm = FormController.create(
      {
        proposalName: new FormControl<string>('', {
          validations: ['printableCharacters'],
        }),
        effectiveDate: new FormControl<string>(''),
      },
      {
        capabilities: [
          {
            type: 'zip',
            params: {
              zip: { optional: false },
              state: {
                customValidations: [
                  {
                    check: this._rootStore.domainStoreV2.isEligibleState,
                    msg: 'State is ineligible',
                  },
                ],
              },
            },
          },
          {
            type: 'sic',
            params: {
              additionalValidations: [
                {
                  check: this._rootStore.domainStoreV2.isEligibleSicCode,
                  msg: 'SIC Code is ineligible',
                },
              ],
            },
          },
        ],
      },
    );

    reaction(
      () => [
        this.proposalForm.fields.effectiveDate.value,
        this.proposalForm.fields.zip.value,
        this.proposalForm.fields.state.value,
        this.proposalForm.fields.county.value,
        this.proposalForm.fields.sic4.value,
      ],
      async () => {
        if (!this._canFetchProductLines) {
          this.domainProductLinesDelegate.clearProductLines();
          return;
        }

        await this.domainProductLinesDelegate.getDomainProductLines(this.proposalForm.getFormDataObject(), true);
      },
    );

    makeAutoObservable(this, { domainProductLinesDelegate: false }, { autoBind: true });
  }
}
